import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  FormModelControl,
  SelectOption,
  SnackbarService,
  TabsGroupComponent,
} from '@bcl/design-system';
import { ActionPointDictionariesService } from 'src/app/pages/action/action-dictionaries.service';
import { ObjectPlacerCoordinatesSelectorData } from '../../../../components';
import { distinctUntilChanged, Subject, takeUntil } from 'rxjs';

type DetectionForm = {
  detectionSourceId: string | null;
  detectionTypeId: string | null;
  detectionCount: number | null;
  detectionMemo: string | null;
  createAction: boolean;
  actionTypeId: string | null;
  actionDeadline: string | null;
  actionDescription: string | null;
};

export type DetectionDialogData = {
  detectionSources: SelectOption[];
  detectionTypes: SelectOption[];
  areaMapData: ObjectPlacerCoordinatesSelectorData;
};

@Component({
  selector: 'app-detection-dialog',
  templateUrl: './detection-dialog.component.html',
  styleUrls: ['./detection-dialog.component.scss'],
})
export class DetectionDialogComponent implements OnDestroy {
  public detectionForm: FormGroup<FormModelControl<DetectionForm>>;
  public detectionSourceOptions: SelectOption[] = [];
  public detectionTypeOptions: SelectOption[] = [];
  public actionTypeOptions: SelectOption[] = [];
  public isLoading = false;
  public isCreatingAction = false;

  public readonly actionMapTabIndex = 0;
  public readonly actionFormTabIndex = 1;
  public actionCoordinatesSelectorData:
    | ObjectPlacerCoordinatesSelectorData
    | undefined;

  @ViewChild('actionTabs')
  public actionTabs!: TabsGroupComponent;
  @ViewChild('createActionQuestion', { read: ElementRef })
  public createActionQuestion!: ElementRef;

  private newActionCoordinates: { x: number; y: number } | undefined;

  private destroyed$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<DetectionDialogComponent>,
    fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: DetectionDialogData,
    private actionDictionariesService: ActionPointDictionariesService,
    private snackbarService: SnackbarService
  ) {
    this.detectionForm = fb.group({
      detectionSourceId: [null as string | null, Validators.required],
      detectionTypeId: [null as string | null, Validators.required],
      detectionCount: [null as number | null, Validators.required],
      detectionMemo: [null as string | null],
      createAction: [false],
      actionTypeId: [null as string | null],
      actionDeadline: [null as string | null],
      actionDescription: [null as string | null],
    });

    if (data) {
      this.detectionSourceOptions = data.detectionSources;
      this.detectionTypeOptions = data.detectionTypes;
    }

    // Load action types
    this.loadActionTypes();

    // Set up conditional validation for action fields
    this.detectionForm
      .get('createAction')
      ?.valueChanges.pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe((createAction) => {
        if (createAction) {
          this.detectionForm
            .get('actionTypeId')
            ?.setValidators([Validators.required]);
          this.detectionForm
            .get('actionDeadline')
            ?.setValidators([Validators.required]);
        } else {
          this.detectionForm.get('actionTypeId')?.clearValidators();
          this.detectionForm.get('actionDeadline')?.clearValidators();
        }

        this.detectionForm.get('actionTypeId')?.updateValueAndValidity();
        this.detectionForm.get('actionDeadline')?.updateValueAndValidity();

        this.actionCoordinatesSelectorData = undefined;
        this.isCreatingAction = createAction;
      });
  }

  public get isActionFormValid(): boolean {
    if (!this.isCreatingAction) {
      return true;
    }

    return !!(
      this.detectionForm.get('actionTypeId')?.valid &&
      this.detectionForm.get('actionDeadline')?.valid
    );
  }

  public onNewActionCoordinatesSelected(coordinates: {
    x: number;
    y: number;
  }): void {
    this.newActionCoordinates = coordinates;
    this.actionTabs.selectTab(this.actionFormTabIndex);
  }

  public onObjectPlacerViewInitialized(): void {
    setTimeout(() => {
      this.actionCoordinatesSelectorData = this.data.areaMapData;
      this.createActionQuestion.nativeElement.scrollIntoView({
        behavior: 'smooth',
      });
    });
  }

  public save(): void {
    if (this.detectionForm.invalid) {
      return;
    }

    this.isLoading = true;
    const formValue = this.detectionForm.value;
    const createAction = formValue.createAction;

    // First create the detection
    const detectionData = {
      detectionSourceId: formValue.detectionSourceId,
      detectionTypeId: formValue.detectionTypeId,
      detectionCount: formValue.detectionCount,
      memo: formValue.detectionMemo,
    };

    if (createAction) {
      // Find the selected action type label to use as title
      const selectedActionType = this.actionTypeOptions.find(
        (option) => option.value === formValue.actionTypeId
      );
      const actionTitle = selectedActionType?.label || '';

      this.dialogRef.close({
        detection: detectionData,
        action: {
          actionTypeId: formValue.actionTypeId,
          deadline: formValue.actionDeadline,
          title: actionTitle,
          description: formValue.actionDescription,
          mapCoordinates: this.newActionCoordinates,
        },
      });
    } else {
      this.dialogRef.close({
        detection: detectionData,
      });
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private loadActionTypes(): void {
    this.actionDictionariesService.getActionTypes().subscribe({
      next: (actionTypes) => {
        this.actionTypeOptions = actionTypes.map((type) => ({
          value: type.value,
          label: type.defaultTitle,
        }));
      },
      error: () => {
        this.snackbarService.open(
          'red',
          'DETECTION_EDIT.MESSAGES.ERROR_LOADING_ACTION_TYPES'
        );
      },
    });
  }
}
